import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@material-tailwind/react";
import _ from "lodash";
import { phone } from "phone";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Quote } from "../../types";
import Button from "../components/Button";
import ConfirmOrCancel from "../components/Modals/ConfirmOrCancel";
import MoneyInput from "../components/MoneyInput";
import { formatDateHTML, formatLabel, formatTimeMilitary } from "../lib/formatters";
import { SocketRPC, socketEmit } from "../lib/socket";
import { useOrders } from "../store/orders";
import useUser from "../store/user";

interface QuoteWindow {
  type: string;
  before: {
    date: string;
    time: string;
  };
  after: {
    date: string;
    time: string;
  };
}


interface EditState {
  reason?: string;
  phone?: string;
  specialInstructions?: string;
  subTotal?: number;
  tip?: number;
  tax?: number;
  isFutureOrder?: boolean;
  futureDeliveryDate?: string;
  futureDeliveryTime?: string;
  quoteWindow?: QuoteWindow;
}

const EXPECTED_DELIVERY_TIME = 15 * 1000 * 60; // assume 15 min for now

const _getQuoteWindow = (quote?: Quote) : QuoteWindow | undefined => {
  if (!quote) return undefined;

  const type = _.get(quote, 'quoteType');
  const before = _.get(quote, 'constraints.before');
	const after = _.get(quote, 'constraints.after');

  return {
    type,
    before: {
      date: formatDateHTML(before),
      time: formatTimeMilitary(before),
    },
    after: {
      date: formatDateHTML(after),
      time: formatTimeMilitary(after),
    },
  }

};

const OrderEditPage = () => {
  const { orders, loading: loadingOrders } = useOrders();
  const { user } = useUser();
  const params = useParams();
  const navigate = useNavigate();
  const [showConfirmEditModal, setShowConfirmEditModal] = useState(false);
  const [confirmEditModalTitle, setConfirmEditModalTitle] = useState('');

  const order = orders[params.orderKey!];

  const defaultOrderDetails = useMemo(
    () => ({
      reason: "",
      phone: order?.consumer?.phone ?? "",
      specialInstructions: order?.consumer?.specialInstructions ?? "",
      subTotal: order?.price?.subTotal ?? undefined,
      tip: order?.price?.tip ?? undefined,
      tax: order?.price?.tax ?? undefined,
      isFutureOrder: order?.time?.isFutureOrder ?? undefined,
      futureDeliveryDate: formatDateHTML(order?.time?.lateDelivery) ?? '',
      futureDeliveryTime: formatTimeMilitary(order?.time?.lateDelivery) ?? '',
      quoteWindow: _getQuoteWindow(order?.quote),
    }),
    [order]
  );

  const [editState, setEditState] = useState<EditState>(defaultOrderDetails);

  useEffect(() => {
    setEditState(defaultOrderDetails);
  }, [order]);

  const buildRequestBody = () => {
    const phoneNumber = phone(editState.phone!);
    
    const req: any = {
      reason: editState.reason,
      order: {
        orderKey: order.orderKey,
        price: {
          subTotal: editState.subTotal,
          tip: editState.tip,
          tax: editState.tax,
        },
        consumer: {
          specialInstructions: editState.specialInstructions,
          phone: phoneNumber.phoneNumber,
        },
      },
      user,
    };

    if (editState.isFutureOrder) {
      if (editState.quoteWindow) {
        req.order.quote = {
          quoteType: editState.quoteWindow.type,
          constraints: {
            before: new Date(`${editState.quoteWindow.before.date}T${editState.quoteWindow.before.time}`),
            after: new Date(`${editState.quoteWindow.after.date}T${editState.quoteWindow.after.time}`),
          }
        }
      } else {
        req.order.time = {
          isFutureOrder: true,
          lateDelivery: new Date(`${editState.futureDeliveryDate}T${editState.futureDeliveryTime}`),
        };
      }
    }
    
    return req;
  };

  const onUpdateOrder = async () => {
    if (!editState.subTotal || !editState.tip || !editState.tax) {
      toast.error("Please ensure all fields are filled out.");
      return;
    }

    const phoneNumericVals = editState.phone!.replace(/\D/g, "");

    if (!phoneNumericVals.includes("5555555555")) {
      const phoneNumber = phone(editState.phone!);

      if (!phoneNumber.isValid) {
        toast.error("Please enter a valid phone number");
        return;
      }
    }

    try {
      const body = buildRequestBody();

      const res = await socketEmit(SocketRPC.TryOrderUpdate, body);

      if (res.success) {
        toast.success("Order edited successfully!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to edit order.");
    }
  };

  if (loadingOrders) {
    return <Spinner />;
  }

  if (!order) {
    return <p className="p-3">Order not found.</p>;
  }

  const QuoteWindowInput: React.FunctionComponent<{dateOrTime: string, beforeOrAfter: string}> = ({
    dateOrTime,
    beforeOrAfter,
  }) => {
    const now = (new Date()).toISOString();
    return (
		<input
			value={editState.quoteWindow![beforeOrAfter][dateOrTime]}
			onChange={(e) =>
				setEditState({
					...editState,
					quoteWindow: {
						...editState.quoteWindow!,
						[beforeOrAfter]: {
							...editState.quoteWindow![beforeOrAfter],
							[dateOrTime]: e.target.value,
						},
					},
				})
			}
			type={dateOrTime}
			min={
				dateOrTime === 'date'
					? formatDateHTML(now) : ''
					// : editState.quoteWindow![beforeOrAfter].date === formatDateHTML(now)
					// ? formatTimeMilitary(now)
					// : '00:00'
			}
			className="block px-3 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
		/>
	);
  }

  return (
    <>
      <Link to={`/active-orders/${order.orderKey}`}>
        <p className="w-3/4 mx-auto text-lg mt-8 flex items-center hover:text-gray-500">
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          <span>Back</span>
        </p>
      </Link>

      <div className="w-3/4 mx-auto mt-8 bg-white">
        <div className="border-2 border-gray-300 shadow-sm rounded-md p-4 mx-auto">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Edit Order
          </h2>

          <p className="text-sm leading-6 text-gray-600">
            Modify the price, consumer information or reason below.
          </p>

          <p className="mt-5 text-lg font-semibold leading-6 text-gray-600">
            Order for {order.consumer.name}
          </p>

          <div className="mt-8 grid grid-cols-3 gap-5">
            <div className="col-span-1">
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Consumer Phone
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  value={editState.phone}
                  onChange={(e) =>
                    setEditState({ ...editState, phone: e.target.value })
                  }
                  className="block px-3 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Special Instructions
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  value={editState.specialInstructions}
                  onChange={(e) =>
                    setEditState({
                      ...editState,
                      specialInstructions: e.target.value,
                    })
                  }
                  className="block px-3 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-5">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Subtotal
              </label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <MoneyInput
                  value={editState.subTotal ?? 0}
                  onChange={(num) =>
                    setEditState({ ...editState, subTotal: num })
                  }
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 sm:text-sm">USD</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Tax
              </label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <MoneyInput
                  value={editState.tax ?? 0}
                  onChange={(num) => setEditState({ ...editState, tax: num })}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 sm:text-sm">USD</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Tip
              </label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <MoneyInput
                  value={editState.tip ?? 0}
                  onChange={(num) => setEditState({ ...editState, tip: num })}
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 sm:text-sm">USD</span>
                </div>
              </div>
            </div>
          </div>

          {(editState.isFutureOrder || editState.quoteWindow ) ? <div className="mt-8">
            <div>
              {editState.quoteWindow && editState.quoteWindow !== undefined ? <> 
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  {`${formatLabel(editState.quoteWindow.type)} Window`}
                </label>
                <div className="mt-2 flex gap-4 items-center">
                <label className="block text-sm font-medium italic leading-6 text-gray-900">
                  {`After: `}
                </label>
                {<QuoteWindowInput dateOrTime={'date'} beforeOrAfter={'after'}/>}
                {<QuoteWindowInput dateOrTime={'time'} beforeOrAfter={'after'}/>}
                </div> 
                <div className="mt-2 flex gap-4 items-center">
                <label className="block text-sm font-medium italic leading-6 text-gray-900">
                  {`Before:`}
                </label>
                {<QuoteWindowInput dateOrTime={'date'} beforeOrAfter={'before'}/>}
                {<QuoteWindowInput dateOrTime={'time'} beforeOrAfter={'before'}/>}
                </div> 
              </> : <>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                  Future Delivery Time 
                </label>
                <div className="mt-2 flex gap-4">
                <input
                    value={editState.futureDeliveryDate}
                    onChange={(e) =>
                      setEditState({ ...editState, futureDeliveryDate: e.target.value })
                    }
                    type="date"
                    className="block px-3 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  <input
                    value={editState.futureDeliveryTime}
                    onChange={(e) =>
                      setEditState({ ...editState, futureDeliveryTime: e.target.value })
                    }
                    type="time"
                    className="block px-3 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div> 
              </>}
            </div>
          </div> : null}

          <div className="mt-8">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Edit Reason
              </label>
              <div className="mt-2">
                <input
                  value={editState.reason}
                  placeholder="Why are you editing this order?"
                  onChange={(e) =>
                    setEditState({ ...editState, reason: e.target.value })
                  }
                  type="text"
                  className="block px-3 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex space-x-3">
            <Button
              type="button"
              className="bg-blue-600 hover:bg-blue-400 text-white"
              label="Update"
              onClick={() => {
                if (editState.isFutureOrder) {
                  const tenMinutesFromNow = new Date(Date.now() + 10 * 1000 * 60);

                  let windowType, windowAfter, pickupAfterTime;
                  if (editState.quoteWindow) {
                    windowAfter = new Date(`${editState.quoteWindow.after.date}T${editState.quoteWindow.after.time}`);
                    windowType = editState.quoteWindow.type;
                    pickupAfterTime = windowType === 'SCHEDULED_PICKUP' ? windowAfter : new Date(windowAfter.getTime() - EXPECTED_DELIVERY_TIME);
                  } else {
                    windowAfter = new Date(`${editState.futureDeliveryDate}T${editState.futureDeliveryTime}`);
                    windowType = 'Future delivery time'
                    pickupAfterTime = new Date(windowAfter.getTime() - EXPECTED_DELIVERY_TIME);
                  }

                  if (pickupAfterTime < tenMinutesFromNow) {
                    setConfirmEditModalTitle(`The selected ${formatLabel(windowType)} window is close to the current time. The order will be marked ready for pickup very soon. Do you wish to proceed?`);
                    setShowConfirmEditModal(true);
                    return;
                  }
                }
                onUpdateOrder()
              }}
            />
            <Link to={`/active-orders/${order.orderKey}`}>
              <Button
                type="button"
                className="bg-red-400 hover:bg-red-300 text-white"
                label="Cancel"
                onClick={() => navigate(`/active-orders/${order.orderKey}`)}
              />
            </Link>
          </div>
        </div>
        <ConfirmOrCancel
          open={showConfirmEditModal}
          toggleOpen={() => setShowConfirmEditModal(!showConfirmEditModal)}
          onConfirm={() => onUpdateOrder()}
          title={confirmEditModalTitle}
        />
      </div>
    </>
  );
};

export default OrderEditPage;